import { useEffect, useState } from "react";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, GripVertical } from "lucide-react";
import Portal from "@/components/Portal";

const EMPTY = { title: "", subtitle: "", image: "", product_slugs: [] };

export default function AdminShopTheLook() {
  const [scenes, setScenes] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [{ data: s }, { data: p }] = await Promise.all([
      axios.get(`${API}/shop-the-look-scenes`),
      axios.get(`${API}/products?limit=500`),
    ]);
    setScenes(s);
    setProducts(p);
  };
  useEffect(() => { load(); }, []);

  const productBySlug = Object.fromEntries(products.map((p) => [p.slug, p]));

  const save = async () => {
    if (!editing.title?.trim() || !editing.image?.trim()) {
      toast.error("Title and image URL are required");
      return;
    }
    try {
      const payload = {
        title: editing.title.trim(),
        subtitle: editing.subtitle || "",
        image: editing.image.trim(),
        product_slugs: editing.product_slugs || [],
      };
      if (editing.id) await axios.put(`${API}/admin/shop-the-look-scenes/${editing.id}`, payload);
      else await axios.post(`${API}/admin/shop-the-look-scenes`, payload);
      toast.success(editing.id ? "Scene updated" : "Scene created");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete scene "${s.title}"?`)) return;
    await axios.delete(`${API}/admin/shop-the-look-scenes/${s.id}`);
    toast.success("Deleted");
    load();
  };

  const toggleProduct = (slug) => {
    const cur = editing.product_slugs || [];
    setEditing({
      ...editing,
      product_slugs: cur.includes(slug) ? cur.filter((x) => x !== slug) : [...cur, slug],
    });
  };

  const move = (i, dir) => {
    const arr = [...editing.product_slugs];
    const to = i + dir;
    if (to < 0 || to >= arr.length) return;
    [arr[i], arr[to]] = [arr[to], arr[i]];
    setEditing({ ...editing, product_slugs: arr });
  };

  return (
    <div className="fade-in" data-testid="admin-shop-the-look">
      <div className="flex justify-between items-end mb-10 flex-wrap gap-4">
        <div>
          <div className="font-micro text-[#8A8680] mb-3">Homepage</div>
          <h1 className="font-serif text-[#EDE4D3] text-5xl">Shop the Look.</h1>
          <p className="text-[#8A8680] mt-3 max-w-lg">Each scene shows a large lifestyle image plus product thumbnails a shopper can tap through to the PDP.</p>
        </div>
        <button className="btn-bronze" onClick={() => setEditing({ ...EMPTY })} data-testid="stl-new-btn">
          <Plus size={14} className="inline mr-2" /> New Scene
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6" data-testid="stl-scenes-grid">
        {scenes.map((s) => (
          <div key={s.id} className="p-5 flex gap-5" style={{ background: "#141414", border: "1px solid var(--hairline)" }} data-testid={`stl-scene-card-${s.id}`}>
            {s.image && <img src={s.image} alt="" className="w-32 h-40 object-cover shrink-0" />}
            <div className="flex-1">
              <div className="font-serif text-[#EDE4D3] text-xl leading-tight">{s.title}</div>
              {s.subtitle && <div className="font-micro text-[#8A8680] mt-2">{s.subtitle}</div>}
              <div className="font-micro text-[#B8895C] mt-3">{(s.product_slugs || []).length} products</div>
              <div className="flex gap-3 mt-4">
                <button onClick={() => setEditing({ ...s })} className="font-micro text-[#EDE4D3] link-underline" data-testid={`stl-edit-${s.id}`}>Edit</button>
                <button onClick={() => del(s)} className="font-micro text-[#8A8680] hover:text-[#B8895C]" data-testid={`stl-del-${s.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
        {scenes.length === 0 && (
          <div className="col-span-2 py-16 text-center font-serif text-[#8A8680] text-2xl" style={{ background: "#141414", border: "1px solid var(--hairline)" }}>
            No scenes yet — create your first.
          </div>
        )}
      </div>

      {editing && (
        <Portal>
          <div
            className="fixed inset-0 z-50 bg-black/80 overflow-y-auto"
            data-testid="stl-editor-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}
          >
            <div className="w-full max-w-3xl mx-auto my-6 p-8 md:p-10" style={{ background: "#141414", border: "1px solid var(--hairline)" }}>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <div className="font-micro text-[#8A8680] mb-1">{editing.id ? "Editing" : "New Scene"}</div>
                  <h2 className="font-serif text-[#EDE4D3] text-3xl">Shop the Look Scene</h2>
                </div>
                <button onClick={() => setEditing(null)} className="text-[#EDE4D3] hover:text-[#B8895C]"><X size={20} strokeWidth={1} /></button>
              </div>

              <div className="space-y-5">
                <input className="input-teak" placeholder="Scene title *" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="stl-title" />
                <input className="input-teak" placeholder="Subtitle" value={editing.subtitle || ""} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} data-testid="stl-subtitle" />
                <input className="input-teak" placeholder="Scene image URL (full URL, e.g. Unsplash) *" value={editing.image || ""} onChange={(e) => setEditing({ ...editing, image: e.target.value })} data-testid="stl-image-url" />
                {editing.image && (
                  <div className="aspect-[4/5] max-w-xs" style={{ background: "#0B0B0C", border: "1px solid var(--hairline)" }}>
                    <img src={editing.image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              <div className="mt-8">
                <div className="font-micro text-[#8A8680] mb-3">Products in this scene ({(editing.product_slugs || []).length})</div>
                {editing.product_slugs?.length > 0 && (
                  <div className="mb-4 space-y-2" data-testid="stl-selected-list">
                    {editing.product_slugs.map((slug, i) => {
                      const p = productBySlug[slug];
                      if (!p) return null;
                      return (
                        <div key={slug} className="flex items-center gap-3 p-2" style={{ background: "#0B0B0C" }}>
                          <GripVertical size={14} className="text-[#8A8680]" />
                          {p.images?.[0] && <img src={p.images[0]} className="w-10 h-12 object-cover" alt="" />}
                          <div className="flex-1 font-serif text-[#EDE4D3] text-sm">{p.name}</div>
                          <button onClick={() => move(i, -1)} disabled={i === 0} className="font-micro text-[#8A8680] disabled:opacity-30">↑</button>
                          <button onClick={() => move(i, 1)} disabled={i === editing.product_slugs.length - 1} className="font-micro text-[#8A8680] disabled:opacity-30">↓</button>
                          <button onClick={() => toggleProduct(slug)} className="text-[#8A8680] hover:text-[#B8895C]"><X size={14} /></button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="font-micro text-[#8A8680] mb-2">Tap to add / remove from scene</div>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2 max-h-72 overflow-y-auto" data-testid="stl-product-picker">
                  {products.filter((p) => p.category !== "swatches").map((p) => {
                    const on = (editing.product_slugs || []).includes(p.slug);
                    return (
                      <button
                        key={p.slug}
                        onClick={() => toggleProduct(p.slug)}
                        className="text-left p-2 transition-colors duration-500"
                        style={{ background: on ? "#B8895C" : "#0B0B0C", border: `1px solid ${on ? "#B8895C" : "var(--hairline)"}` }}
                        data-testid={`stl-product-${p.slug}`}
                      >
                        <div className="aspect-square mb-2" style={{ background: "#141414" }}>
                          {p.images?.[0] && <img src={p.images[0]} className="w-full h-full object-cover" alt="" />}
                        </div>
                        <div className={`font-micro text-[10px] leading-tight ${on ? "text-[#0B0B0C]" : "text-[#D8D6D1]"}`}>{p.name}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-8 flex gap-3">
                <button className="btn-bronze flex-1" onClick={save} data-testid="stl-save">Save Scene</button>
                <button className="btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
